import { Injectable, inject } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import type { MessagePayload } from 'firebase/messaging';
import Swal from 'sweetalert2';
import { STORAGE_KEYS } from '../../config/storage-keys.config';
import { NotificationService } from '../notification/notification.service';
import { PushNotificationApiService } from '../push-notification/push-notification-api.service';
import { FirebaseMessagingService } from './firebase-messaging.service';

/**
 * Orchestrates post-auth Firebase web push setup:
 * permission → FCM token → API Gateway registration → foreground listener.
 * Idempotent for a single browser session.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseNotificationInitService {
  private readonly firebaseMessaging = inject(FirebaseMessagingService);
  private readonly pushApi = inject(PushNotificationApiService);
  private readonly notificationService = inject(NotificationService);

  private initializePromise: Promise<void> | null = null;
  private foregroundListenerAttached = false;
  private lastRegisteredToken: string | null = null;

  /**
   * Safe to call multiple times. Concurrent callers share one in-flight init.
   */
  initialize(): Promise<void> {
    if (!this.initializePromise) {
      this.initializePromise = this.runInitialize().catch((error) => {
        console.warn(
          '[FirebaseNotificationInit] Initialization failed gracefully.',
          error,
        );
      });
    }
    return this.initializePromise;
  }

  /** Returns the last known FCM token (memory or storage). */
  getStoredFcmToken(): string | null {
    if (this.lastRegisteredToken) {
      return this.lastRegisteredToken;
    }
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(STORAGE_KEYS.FCM_DEVICE_TOKEN);
  }

  /**
   * Deactivates the current device token via API Gateway. Never throws.
   * Clears local FCM token storage afterward.
   */
  async deactivateCurrentToken(): Promise<void> {
    const token = this.getStoredFcmToken();
    this.firebaseMessaging.stopForegroundListener();
    this.foregroundListenerAttached = false;
    this.initializePromise = null;
    this.lastRegisteredToken = null;

    if (!token) {
      this.clearStoredToken();
      return;
    }

    try {
      await firstValueFrom(
        this.pushApi.deactivateDeviceToken({ fcmToken: token }).pipe(
          catchError(() => of(null)),
        ),
      );
    } catch {
      // Logout must not be blocked.
    } finally {
      this.clearStoredToken();
    }
  }

  private async runInitialize(): Promise<void> {
    if (!this.firebaseMessaging.isBrowserSupported()) {
      return;
    }
    if (!this.firebaseMessaging.isSecureContext()) {
      console.warn(
        '[FirebaseNotificationInit] Skipping FCM — insecure context.',
      );
      return;
    }

    const permission = await this.firebaseMessaging.requestPermission();
    if (permission !== 'granted') {
      return;
    }

    const token = await this.firebaseMessaging.getRegistrationToken();
    if (!token) {
      return;
    }

    await this.registerTokenIfNeeded(token);
    await this.attachForegroundListener();
  }

  private async registerTokenIfNeeded(token: string): Promise<void> {
    if (this.lastRegisteredToken === token) {
      this.persistToken(token);
      return;
    }

    const stored =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(STORAGE_KEYS.FCM_DEVICE_TOKEN)
        : null;
    if (stored === token && this.lastRegisteredToken === token) {
      return;
    }

    try {
      await firstValueFrom(
        this.pushApi.registerDeviceToken({
          fcmToken: token,
          platform: 'WEB',
          browser: this.detectBrowser(),
        }),
      );
      this.lastRegisteredToken = token;
      this.persistToken(token);
    } catch (error) {
      console.warn(
        '[FirebaseNotificationInit] Device token registration failed.',
        error,
      );
    }
  }

  private async attachForegroundListener(): Promise<void> {
    if (this.foregroundListenerAttached) {
      return;
    }

    await this.firebaseMessaging.listenForForegroundMessages((payload) => {
      this.handleForegroundMessage(payload);
    });
    this.foregroundListenerAttached = true;
  }

  private handleForegroundMessage(payload: MessagePayload): void {
    try {
      const title =
        payload.notification?.title ||
        payload.data?.['title'] ||
        'New notification';
      const body =
        payload.notification?.body || payload.data?.['body'] || undefined;

      void Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title,
        text: body,
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
      });

      this.notificationService.refresh();
    } catch (error) {
      console.warn(
        '[FirebaseNotificationInit] Foreground message handling failed.',
        error,
      );
    }
  }

  private persistToken(token: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.FCM_DEVICE_TOKEN, token);
    }
  }

  private clearStoredToken(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.FCM_DEVICE_TOKEN);
    }
  }

  private detectBrowser(): string {
    if (typeof navigator === 'undefined') {
      return 'Unknown';
    }
    const ua = navigator.userAgent;
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome/')) return 'Chrome';
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    return 'Unknown';
  }
}
